import { Field, FieldLabel, FieldSelect } from '@ibe/ui';
import { type QueryKey } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';

import { useApiQuery } from '../../lib/api/hooks.js';

/**
 * A foreign key, chosen by name.
 *
 * Bound to an `/options` endpoint rather than a list: `/options` answers
 * `{value, label}` pairs, already narrowed to what may actually be chosen and
 * already scoped to the caller. A picker built over a list endpoint has to
 * decide for itself what "choosable" means, and every picker decides
 * differently.
 *
 * The search term is sent to the server rather than filtered here. The
 * endpoint has a ceiling, and a client-side filter over the first fifty rows
 * silently hides the fifty-first — a property an operator can see in the list
 * and cannot select in the form.
 */

const optionSchema = z.array(z.object({ value: z.string(), label: z.string() }));

export interface EntitySelectProps {
  /** The `/options` path, without a query string. */
  readonly path: string;
  /** Cache key. Build it from `queryKeys`, never inline. */
  readonly queryKey: QueryKey;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly label: string;
  readonly error?: string | undefined;
  readonly description?: string | undefined;
  readonly required?: boolean;
  readonly placeholder?: string;
  /** Extra parameters, e.g. the property a policy list is scoped to. */
  readonly params?: Readonly<Record<string, string>> | undefined;
  /** False while a parent value this depends on is unchosen. */
  readonly enabled?: boolean;
  /** Shown in place of the control's help text when `enabled` is false. */
  readonly disabledHint?: string;
  readonly readOnly?: boolean;
}

export function EntitySelect({
  path,
  queryKey,
  value,
  onChange,
  label,
  error,
  description,
  required = false,
  placeholder,
  params,
  enabled = true,
  disabledHint,
  readOnly = false,
}: EntitySelectProps): React.JSX.Element {
  const [search, setSearch] = useState('');

  const query = useApiQuery({
    queryKey: [...queryKey, params ?? {}, search],
    path,
    schema: optionSchema,
    query: { ...(params ?? {}), ...(search === '' ? {} : { q: search }) },
    enabled,
  });

  const options = query.data ?? [];

  // The chosen value must always be offerable, or the control shows a blank
  // button for a value the record genuinely holds — which is what happens the
  // moment a search narrows the list past it, or the row falls outside the
  // page the endpoint returned.
  const chosen = options.some((option) => option.value === value);
  const offered =
    value === '' || chosen ? options : [{ value, label: 'Currently selected' }, ...options];

  return (
    <Field
      error={error}
      required={required}
      description={enabled ? description : (disabledHint ?? description)}
    >
      <FieldLabel>{label}</FieldLabel>
      <FieldSelect
        value={value}
        onValueChange={onChange}
        onSearchChange={(next: string) => {
          setSearch(next);
        }}
        options={offered}
        disabled={!enabled || readOnly || query.isPending}
        searchable
        {...(placeholder === undefined ? {} : { placeholder })}
      />
    </Field>
  );
}
